import { useState, useEffect } from "react";
import "./App.css";

export default function App() {
  const [hour, setHour] = useState(12); // integer 1..12
  const [minute, setMinute] = useState(0); // 0..59
  const [dragging, setDragging] = useState(null); // 'hour' | 'minute' | null

  // learner-friendly labels (kept for phrase)
  const irishNums = {
    1: "a haon",
    2: "a dó",
    3: "a trí",
    4: "a ceathair",
    5: "a cúig",
    6: "a sé",
    7: "a seacht",
    8: "a hocht",
    9: "a naoi",
    10: "a deich",
    11: "a haon déag",
    12: "a dó dhéag",
  };

  const irishMinutes = {
    5: "cúig",
    10: "deich",
    15: "ceathrú",
    20: "fiche",
    25: "fiche a cúig",
    30: "leath",
  };

  const engNums = {
    1: "one",
    2: "two",
    3: "three",
    4: "four",
    5: "five",
    6: "six",
    7: "seven",
    8: "eight",
    9: "nine",
    10: "ten",
    11: "eleven",
    12: "twelve",
  };

  const engMinutes = {
    5: "five",
    10: "ten",
    15: "quarter",
    20: "twenty",
    25: "twenty-five",
    30: "half",
  };

  const getPhrase = (h, m) => {
    const rounded = Math.round(m / 5) * 5; // learner-friendly snapping
    const nextHour = (h % 12) + 1;
    if (rounded === 0) return `${irishNums[h]} a chlog – ${engNums[h]} o’clock`;
    if (rounded === 30)
      return `Leath i ndiaidh ${irishNums[h]} – half past ${engNums[h]}`;
    if (rounded < 30)
      return `${irishMinutes[rounded]} i ndiaidh ${irishNums[h]} – ${engMinutes[rounded]} past ${engNums[h]}`;
    return `${irishMinutes[60 - rounded]} go dtí ${irishNums[nextHour]} – ${engMinutes[60 - rounded]} to ${engNums[nextHour]}`;
  };

  const currentPhrase = getPhrase(hour === 12 ? 12 : hour % 12, minute);

  // pointer -> angle helper
  const updateFromCoords = (clientX, clientY) => {
    const rect = document.querySelector(".clock-face").getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = clientX - cx;
    const dy = clientY - cy;
    // angle where 0 = top (12), increases clockwise
    let angle = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
    if (angle < 0) angle += 360;

    if (dragging === "minute") {
      // snap to nearest 5-minute slot (learner-friendly)
      const slot = Math.round(angle / 30); // 0..11
      const newMinute = (slot * 5) % 60;
      setMinute(newMinute);
    } else if (dragging === "hour") {
      // derive hour & minute from exact angle, minutes snapped to 5
      const exactHour = angle / 30; // 0..12
      let baseHour = Math.floor(exactHour);
      if (baseHour === 0) baseHour = 12;
      const fractional = exactHour - Math.floor(exactHour);
      const snappedMinute = (Math.round((fractional * 60) / 5) * 5) % 60;
      setHour(baseHour);
      setMinute(snappedMinute);
    }
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    updateFromCoords(e.clientX, e.clientY);
  };

  const handleTouchMove = (e) => {
    if (!dragging) return;
    const t = e.touches[0];
    updateFromCoords(t.clientX, t.clientY);
  };

  useEffect(() => {
    const handleUp = () => setDragging(null);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("touchend", handleUp);
    window.addEventListener("touchmove", handleTouchMove);
    return () => {
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("touchend", handleUp);
      window.removeEventListener("touchmove", handleTouchMove);
    };
  }, [dragging]);

  const resetToCurrentTime = () => {
    const now = new Date();
    const h = now.getHours() % 12 || 12;
    const m = Math.round(now.getMinutes() / 5) * 5;
    setHour(h);
    setMinute(m);
  };

  // angles for rendering
  const hourAngle = ((hour % 12) * 30) + minute / 2; // smooth hour hand
  const minuteAngle = minute * 6;

  const digitalHour = hour % 12 === 0 ? 12 : hour % 12;
  const digitalMinute = minute.toString().padStart(2, "0");

  return (
    <div className="app-container">
      <h1 className="title">An Chloig</h1>

      <div className="clock-face" role="application" aria-label="Analog clock">
        {/* Numbers: rotate container → translate outward → rotate back to keep upright */}
        {[...Array(12)].map((_, i) => {
          const number = i === 0 ? 12 : i;
          const outerAngle = i * 30; // degrees
          return (
            <div
              key={i}
              className="number-container"
              style={{ transform: `rotate(${outerAngle}deg)` }}
            >
              <div
                className="clock-number"
                style={{ transform: `translateY(-120px) rotate(-${outerAngle}deg)` }}
                onMouseDown={() => {
                  // quick-set hour by clicking numbers (keeps minute as-is)
                  setHour(number);
                }}
              >
                {number}
              </div>
            </div>
          );
        })}

        {/* Tick marks every 5 minutes */}
        {[...Array(12)].map((_, i) => {
          const outerAngle = i * 30;
          return (
            <div
              key={`tick-${i}`}
              className="tick-container"
              style={{ transform: `rotate(${outerAngle}deg)` }}
              aria-hidden
            >
              <div className="clock-tick" style={{ transform: `translateY(-150px)` }} />
            </div>
          );
        })}

        {/* Hour hand */}
        <div
          className="hour-hand"
          style={{ transform: `translate(-50%, 0) rotate(${hourAngle}deg)` }}
          onMouseDown={() => setDragging("hour")}
          onTouchStart={() => setDragging("hour")}
        />

        {/* Minute hand */}
        <div
          className="minute-hand"
          style={{ transform: `translate(-50%, 0) rotate(${minuteAngle}deg)` }}
          onMouseDown={() => setDragging("minute")}
          onTouchStart={() => setDragging("minute")}
        />
      </div>

      <div className="digital-time">⏱️ {digitalHour}:{digitalMinute}</div>
      <div className="phrase">{currentPhrase}</div>

      <button className="reset-btn" onClick={resetToCurrentTime}>
        Athshocraigh go dtí an t-am reatha
      </button>
    </div>
  );
}
